import {
  Condition,
  DateOption,
} from "@redotech/redo-model/return-flow/condition";
import * as React from "react";
import { ConditionType, ConditionTypeDetailsProps } from "../condition";

export interface DateConditionState {
  start: Date | null;
  end: Date | null;
  dateOption: DateOption | null;
}

export const validateDateOptions = (state: DateConditionState) => {
  if (state.dateOption === null) {
    return false;
  }
  if (
    state.dateOption === DateOption.AFTER ||
    state.dateOption === DateOption.BEFORE
  ) {
    return !!state.start;
  } else {
    // Between
    return !!state.start && !!state.end;
  }
};

const getDateDescription = (
  state: DateConditionState,
  dateType: "Order" | "Return" | "Request",
) => {
  if (
    state.dateOption === DateOption.AFTER ||
    state.dateOption === DateOption.BEFORE
  ) {
    return `${dateType} created ${
      state.dateOption === DateOption.AFTER ? "after" : "before"
    } ${state.start?.toLocaleDateString("en-us", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  } else if (state.dateOption === DateOption.BETWEEN) {
    return `${dateType} created between ${state.start?.toLocaleDateString(
      "en-us",
      { month: "short", day: "numeric" },
    )} — ${state.end?.toLocaleDateString("en-us", {
      month: "short",
      day: "numeric",
    })}`;
  } else {
    return `${dateType} created date`;
  }
};

export const RETURN_DATE = (
  Details: React.ComponentType<ConditionTypeDetailsProps<DateConditionState>>,
): ConditionType<DateConditionState, Condition.ReturnDate> => ({
  name: "Request created date",
  description(state) {
    return getDateDescription(state, "Request");
  },
  Details(props: ConditionTypeDetailsProps<DateConditionState>) {
    return React.createElement(Details, props);
  },
  empty: { start: null, end: null, dateOption: null },
  fromModel(model) {
    return {
      start:
        model.start &&
        new Date(
          model.start.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        ),
      end:
        model.end &&
        new Date(
          model.end.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        ),
      dateOption: model.dateOption,
    };
  },
  toModel(state) {
    return {
      type: Condition.RETURN_DATE,
      start:
        state.start &&
        state.start
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      end:
        state.end &&
        state.end
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      dateOption: state.dateOption,
    };
  },
  valid(state) {
    return validateDateOptions(state);
  },
});

export const ORDER_DATE = (
  Details: React.ComponentType<ConditionTypeDetailsProps<DateConditionState>>,
): ConditionType<DateConditionState, Condition.OrderDate> => ({
  name: "Order created date",
  description(state) {
    return getDateDescription(state, "Order");
  },
  Details(props: ConditionTypeDetailsProps<DateConditionState>) {
    return React.createElement(Details, props);
  },
  empty: { start: null, end: null, dateOption: null },
  fromModel(model) {
    return {
      start:
        model.start &&
        new Date(
          model.start.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        ),
      end:
        model.end &&
        new Date(
          model.end.toZonedDateTime(
            Temporal.Now.timeZoneId(),
          ).epochMilliseconds,
        ),
      dateOption: model.dateOption,
    };
  },
  toModel(state) {
    return {
      type: Condition.ORDER_DATE,
      start:
        state.start &&
        state.start
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      end:
        state.end &&
        state.end
          .toTemporalInstant()
          .toZonedDateTimeISO(Temporal.Now.timeZoneId())
          .toPlainDate(),
      dateOption: state.dateOption,
    };
  },
  valid(state) {
    return validateDateOptions(state);
  },
});
